/**
 * What a visitor or non-member chose before the offer: the program or Journey
 * they tapped Start on (web entrance "choose a program"), or the Journey that
 * guided onboarding recommended. Signup, the auth callback, checkout success
 * and PremiumWelcome read it back so the new member lands in that choice.
 *
 * Stored in localStorage (a magic link can open in a new tab), expires after
 * 24 hours, and the return path must be a same-origin program, session or
 * Journey route; anything else falls back to /home.
 */
import { normalizeSafeSameOriginPath } from '@/utils/urlPrivacy';

const STORAGE_KEY = 'jp:start-intent';
export const START_INTENT_MAX_AGE_MS = 24 * 60 * 60 * 1000;

export type StartIntentKind = 'program' | 'journey';

export interface StartIntent {
  kind: StartIntentKind;
  id: string;
  returnPath: string;
  /** Where the Start happened, for analytics (e.g. 'program_landing'). */
  source: string;
  createdAt: string;
}

const ALLOWED_RETURN = /^\/(program|session|p|journey|j)\/[A-Za-z0-9-]{1,100}(\/(start|learn|overview))?(\?[^#]*)?$/;

/** A safe, same-origin program/session/Journey path, else the fallback. */
export function safeStartReturnPath(path: string | null | undefined, fallback = '/home'): string {
  const normalized = normalizeSafeSameOriginPath(path, fallback);
  return ALLOWED_RETURN.test(normalized) ? normalized : fallback;
}

function isIntent(value: unknown): value is StartIntent {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  const v = value as Record<string, unknown>;
  return (
    (v.kind === 'program' || v.kind === 'journey') &&
    typeof v.id === 'string' &&
    v.id.length > 0 &&
    typeof v.returnPath === 'string' &&
    typeof v.source === 'string' &&
    typeof v.createdAt === 'string'
  );
}

export function saveStartIntent(input: Omit<StartIntent, 'createdAt' | 'returnPath'> & { returnPath: string }, now = Date.now()): void {
  if (typeof window === 'undefined') return;
  const intent: StartIntent = {
    ...input,
    returnPath: safeStartReturnPath(input.returnPath),
    createdAt: new Date(now).toISOString(),
  };
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(intent));
  } catch {
    // Storage can be unavailable (private browsing). The offer still works.
  }
}

/** The saved intent if it is fresh and valid; stale or malformed ones are cleared. */
export function readStartIntent(now = Date.now()): StartIntent | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    const created = isIntent(parsed) ? Date.parse(parsed.createdAt) : NaN;
    if (!isIntent(parsed) || !Number.isFinite(created) || now - created > START_INTENT_MAX_AGE_MS || created > now + 60_000) {
      window.localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return { ...parsed, returnPath: safeStartReturnPath(parsed.returnPath) };
  } catch {
    return null;
  }
}

export function clearStartIntent(): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}

/** Read and clear in one step, for the place that finally navigates. */
export function consumeStartIntent(now = Date.now()): StartIntent | null {
  const intent = readStartIntent(now);
  if (intent) clearStartIntent();
  return intent;
}
